
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Check, User, Mail, Building2, Briefcase } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";

// Form schema with validation
const formSchema = z.object({
  fullName: z.string().min(2, {
    message: "Full name must be at least 2 characters."
  }),
  email: z.string().email({
    message: "Please enter a valid email address."
  }),
  organization: z.string().min(2, {
    message: "Organization name must be at least 2 characters."
  }),
  position: z.string().min(2, {
    message: "Position must be at least 2 characters."
  }),
  industrySector: z.string({
    required_error: "Please select an industry sector."
  }),
  otherSector: z.string().optional(),
  contribution: z.enum(["pilot", "advisory", "updates", "technical", "industry", "survey"], {
    required_error: "Please select how you would like to contribute."
  }),
  message: z.string().optional()
});

type FormValues = z.infer<typeof formSchema>;

interface PilotApplicationFormProps {
  onClose: () => void;
  onSubmitSuccess?: () => void;
  formTitle?: string;
  formDescription?: string;
  formType?: "pilot" | "contribute";
}

export function PilotApplicationForm({
  onClose,
  onSubmitSuccess,
  formTitle = "Join the TagMusic Pilot Program",
  formDescription = "Apply to join our pilot program and help shape the future of AI in music",
  formType = "pilot"
}: PilotApplicationFormProps) {
  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      organization: "",
      position: "",
      industrySector: "",
      otherSector: "",
      contribution: undefined,
      message: ""
    }
  });

  // Check if "Other" sector is selected to show additional field
  const selectedSector = form.watch("industrySector");
  const showOtherField = selectedSector === "Other";

  // Handle form submission
  const onSubmit = async (data: FormValues) => {
    try {
      // Prepare data for Supabase
      const contributionData = {
        full_name: data.fullName,
        email: data.email,
        organization: data.organization,
        position: data.position,
        industry_sector: data.industrySector,
        other_sector: data.otherSector,
        contribution_type: data.contribution,
        message: data.message
      };

      // Save to Supabase
      const { error: supabaseError } = await supabase
        .from('framework_contributions')
        .insert([contributionData]);

      if (supabaseError) {
        console.error("Supabase error:", supabaseError);
        throw new Error("Failed to save your application. Please try again.");
      }

      // Send email notification
      const response = await fetch('https://saiuvgbzhndvxmobkway.supabase.co/functions/v1/send-contribution-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contributionData)
      });

      if (!response.ok) {
        console.warn("Email notification might not have been sent, but data was saved.");
      }

      toast({
        title: "Application submitted successfully!",
        description: "We'll review your application and get back to you soon."
      });
      
      if (onSubmitSuccess) {
        onSubmitSuccess();
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Error",
        description: error.message || "There was an error submitting your application",
        variant: "destructive"
      });
    }
  };

  return <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold">{formTitle}</h2>
        <p className="text-muted-foreground">{formDescription}</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Full Name */}
          <FormField control={form.control} name="fullName" render={({
          field
        }) => <FormItem>
                <FormLabel>
                  Full Name <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Input placeholder="Enter your full name" className="pl-10" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {/* Email Address */}
          <FormField control={form.control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel>
                  Email Address <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Input placeholder="Enter your professional email" className="pl-10" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {/* Organization */}
          <FormField control={form.control} name="organization" render={({
          field
        }) => <FormItem>
                <FormLabel>
                  Organization <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Input placeholder="Enter your organization name" className="pl-10" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {/* Position */}
          <FormField control={form.control} name="position" render={({
          field
        }) => <FormItem>
                <FormLabel>
                  Your Position <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Input placeholder="Enter your role/position" className="pl-10" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {/* Industry Sector */}
          <FormField control={form.control} name="industrySector" render={({
          field
        }) => <FormItem>
                <FormLabel>
                  Industry Sector <span className="text-red-500">*</span>
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your sector" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Music Label">Music Label</SelectItem>
                    <SelectItem value="Music Publisher">Music Publisher</SelectItem>
                    <SelectItem value="Distributor">Distributor</SelectItem>
                    <SelectItem value="AI company">AI company</SelectItem>
                    <SelectItem value="AI ecosystem solution provider">AI ecosystem solution provider</SelectItem>
                    <SelectItem value="Creators">Creators</SelectItem>
                    <SelectItem value="DSPs">DSPs</SelectItem>
                    <SelectItem value="PRO">PRO</SelectItem>
                    <SelectItem value="Legal Expert">Legal Expert</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />

          {/* Show "Other" field conditionally */}
          {showOtherField && <FormField control={form.control} name="otherSector" render={({
          field
        }) => <FormItem>
                  <FormLabel>Please specify</FormLabel>
                  <FormControl>
                    <Input placeholder="Please specify your industry sector" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />}

          {/* Contribution Type - Different options based on formType */}
          <FormField control={form.control} name="contribution" render={({
          field
        }) => <FormItem className="space-y-3">
                <FormLabel>
                  How would you like to contribute? <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  {formType === "contribute" ? (
                    <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-col space-y-1">
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="technical" />
                        </FormControl>
                        <FormLabel className="font-normal">Technical Contributor</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="industry" />
                        </FormControl>
                        <FormLabel className="font-normal">Join Industry Advisory Panel</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="survey" />
                        </FormControl>
                        <FormLabel className="font-normal">Fill the survey</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  ) : (
                    <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-col space-y-1">
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="pilot" />
                        </FormControl>
                        <FormLabel className="font-normal">Join as a Pilot Partner</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="advisory" />
                        </FormControl>
                        <FormLabel className="font-normal">Advisory Role</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="updates" />
                        </FormControl>
                        <FormLabel className="font-normal">Updates Only</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {/* Additional Message */}
          <FormField control={form.control} name="message" render={({
          field
        }) => <FormItem>
                <FormLabel>Additional Message</FormLabel>
                <FormControl>
                  <Textarea placeholder="Share any additional information or specific interests" className="resize-none" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-tagblue hover:bg-tagblue/90">
              Submit Application
            </Button>
          </div>

          <div className="text-xs text-muted-foreground mt-6">
            <p>By submitting this form, you give your consent to participate in the AI Music Framework paper. We'll review your application and contact you with further details.</p>
          </div>
        </form>
      </Form>
    </div>;
}

export default PilotApplicationForm;
